/**
 * HYPSEYA — Practice Overlay (formerly Regulation Anchor)
 * Full-screen guided practice player. Renders any phase sequence from the
 * practice catalog (served by /api/training/practices); defaults to the
 * Regulation Anchor when no practice prop is given.
 */
import { useState, useEffect, useRef } from 'react';

// Default practice — kept in sync with `regulation_anchor` in the backend
// catalog so the overlay works standalone (e.g. quick-anchor button).
const DEFAULT_PHASES = [
  {
    id: 'arrive',
    label: 'Arrive',
    duration: 15,
    instruction: 'Stop.\nLet your system\nrecognise this moment.',
    sub: 'You don\'t need to do anything. Just arrive.',
    color: '#1E6B68',
  },
  {
    id: 'breathe',
    label: 'Breathe',
    duration: 32,
    instruction: 'Box Breathing',
    sub: 'Inhale 4 · Hold 4 · Exhale 4 · Hold 4',
    color: '#2D8B87',
    breathing: {
      stages: [
        { label: 'Inhale', count: 4 },
        { label: 'Hold',   count: 4 },
        { label: 'Exhale', count: 4 },
        { label: 'Hold',   count: 4 },
      ],
    },
  },
  {
    id: 'ground',
    label: 'Ground',
    duration: 30,
    instruction: '5 things you see.\n4 you can touch.\n3 you can hear.',
    sub: 'Let your senses anchor you here.',
    color: '#4AACA8',
  },
  {
    id: 'return',
    label: 'Return',
    duration: 15,
    instruction: 'Your nervous system\nhas stabilised.',
    sub: 'You can continue. You are here.',
    color: '#1E3A39',
  },
];

function hexToRgba(hex, alpha) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// Guided breath cycle with arbitrary stage counts (box, 4-7-8, cyclic sigh…)
function BreathGuide({ stages, color }) {
  const [second, setSecond] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setSecond(s => s + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Derive current stage and countdown from a single elapsed counter
  const cycleLen = stages.reduce((s, st) => s + st.count, 0);
  const pos = second % cycleLen;
  let acc = 0;
  let stageIdx = 0;
  let intoStage = 0;
  for (let i = 0; i < stages.length; i++) {
    if (pos < acc + stages[i].count) { stageIdx = i; intoStage = pos - acc; break; }
    acc += stages[i].count;
  }

  const stage = stages[stageIdx];
  const tick = stage.count - intoStage;
  const progress = intoStage / stage.count;
  // Circle expands on any inhale/hold-after-inhale, contracts on exhale
  const isExhale = /exhale|out/i.test(stage.label);
  const circleScale = 0.6 + (isExhale ? (1 - progress) * 0.4 : progress * 0.4);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, marginTop: 12 }}>
      <div style={{ width: 120, height: 120, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
          width: `${circleScale * 120}px`, height: `${circleScale * 120}px`,
          borderRadius: '50%', background: hexToRgba(color, 0.2),
          border: `2px solid ${hexToRgba(color, 0.5)}`,
          transition: 'width 0.9s ease, height 0.9s ease',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: '2.2rem', color, fontWeight: '700' }}>{tick}</span>
        </div>
      </div>
      <div style={{ fontSize: '1.1rem', fontWeight: '700', color, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{stage.label}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        {stages.map((_, i) => (
          <div key={i} style={{
            width: 8, height: 8, borderRadius: '50%',
            background: i === stageIdx ? color : hexToRgba(color, 0.25),
            transition: 'background 0.3s',
          }} />
        ))}
      </div>
    </div>
  );
}

export default function AnchorOverlay({ onClose, practice = null }) {
  const phases = practice?.phases?.length ? practice.phases : DEFAULT_PHASES;
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [done, setDone] = useState(false);
  const [visible, setVisible] = useState(false);

  const phase = phases[phaseIdx];
  const phaseComplete = elapsed >= phase.duration;
  const progress = Math.min(elapsed / phase.duration, 1);
  const phaseBg = `radial-gradient(ellipse at center, ${hexToRgba(phase.color, 0.18)} 0%, transparent 70%)`;

  useEffect(() => { setTimeout(() => setVisible(true), 30); }, []);

  // Tick the phase clock. The updater must stay pure — StrictMode invokes it
  // twice, so any side effect here (like advancing the phase) runs double.
  useEffect(() => {
    if (done) return;
    setElapsed(0);
    const id = setInterval(() => setElapsed(e => e + 0.1), 100);
    return () => clearInterval(id);
  }, [phaseIdx, done]);

  // Advance to the next phase (or finish) once the clock crosses the duration.
  useEffect(() => {
    if (!phaseComplete || done) return;
    const isLast = phaseIdx >= phases.length - 1;
    const t = setTimeout(() => {
      if (isLast) setDone(true);
      else setPhaseIdx(phaseIdx + 1);
    }, isLast ? 800 : 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [phaseComplete, done, phaseIdx]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 350);
  };

  const totalDuration = phases.reduce((s, p) => s + p.duration, 0);
  const totalElapsed = phases.slice(0, phaseIdx).reduce((s, p) => s + p.duration, 0) + Math.min(elapsed, phase.duration);
  const overallProgress = totalElapsed / totalDuration;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9000,
      background: 'linear-gradient(160deg, #0F2421 0%, #0A1A19 55%, #12221F 100%)',
      display: 'flex', flexDirection: 'column',
      opacity: visible ? 1 : 0, transition: 'opacity 0.35s ease',
    }}>
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: phaseBg, transition: 'background 1.2s ease' }} />

      {/* Overall progress bar */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'rgba(255,255,255,0.08)' }}>
        <div style={{ height: '100%', width: `${overallProgress * 100}%`, background: phase.color, transition: 'width 0.1s linear' }} />
      </div>

      <button onClick={handleClose} style={{
        position: 'absolute', top: 20, right: 20, zIndex: 10,
        background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 20, padding: '8px 16px', color: 'rgba(255,255,255,0.5)',
        fontSize: '0.78rem', cursor: 'pointer', letterSpacing: '0.06em',
      }}>Close</button>

      {/* Practice name */}
      {practice?.name && (
        <div style={{
          position: 'absolute', top: 22, left: 24,
          fontSize: '0.72rem', letterSpacing: '0.14em', textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.35)', fontWeight: '600',
        }}>{practice.name}</div>
      )}

      {/* Phase dots */}
      <div style={{ position: 'absolute', top: 24, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 8 }}>
        {phases.map((p, i) => (
          <div key={p.id} style={{
            height: 3, width: i === phaseIdx ? 28 : 10, borderRadius: 2,
            background: i < phaseIdx ? phase.color : i === phaseIdx ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.15)',
            transition: 'all 0.4s ease',
          }} />
        ))}
      </div>

      {!done ? (
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          padding: '60px 32px 80px', textAlign: 'center',
        }}>
          <div style={{
            fontSize: '0.7rem', letterSpacing: '0.16em', textTransform: 'uppercase',
            color: phase.color, fontWeight: '700', marginBottom: 24,
            opacity: 0, animation: 'hyp-fade-up 0.6s ease 0.1s forwards',
          }}>{phase.label}</div>

          <div style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(1.4rem, 5vw, 2rem)', color: 'rgba(255,255,255,0.92)',
            fontWeight: '500', lineHeight: 1.6, whiteSpace: 'pre-line', marginBottom: 16,
            opacity: 0, animation: 'hyp-fade-up 0.7s ease 0.2s forwards',
          }}>{phase.instruction}</div>

          {phase.breathing?.stages?.length ? (
            <div key={phase.id} style={{ opacity: 0, animation: 'hyp-fade-up 0.7s ease 0.4s forwards' }}>
              <BreathGuide stages={phase.breathing.stages} color={phase.color} />
            </div>
          ) : (
            <div style={{
              fontSize: '0.88rem', color: 'rgba(255,255,255,0.45)',
              maxWidth: 280, lineHeight: 1.7,
              opacity: 0, animation: 'hyp-fade-up 0.7s ease 0.5s forwards',
            }}>{phase.sub}</div>
          )}

          {/* Timer arc */}
          <div style={{ marginTop: 48, position: 'relative', width: 56, height: 56 }}>
            <svg width="56" height="56" viewBox="0 0 56 56">
              <circle cx="28" cy="28" r="24" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
              <circle cx="28" cy="28" r="24" fill="none" stroke={phase.color} strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 24}`}
                strokeDashoffset={`${2 * Math.PI * 24 * (1 - progress)}`}
                transform="rotate(-90 28 28)"
                style={{ transition: 'stroke-dashoffset 0.1s linear' }}
              />
            </svg>
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.82rem', color: 'rgba(255,255,255,0.4)', fontWeight: '600',
            }}>{Math.max(0, Math.ceil(phase.duration - elapsed))}s</div>
          </div>
        </div>
      ) : (
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          padding: '60px 32px', textAlign: 'center',
        }}>
          <div style={{
            fontFamily: "'Playfair Display', serif", fontSize: '1.8rem',
            color: 'rgba(255,255,255,0.9)', fontWeight: '500', marginBottom: 12,
            opacity: 0, animation: 'hyp-fade-up 0.8s ease 0.1s forwards',
          }}>Your system has stabilised.</div>
          <div style={{
            fontSize: '0.9rem', color: 'rgba(255,255,255,0.45)',
            maxWidth: 260, lineHeight: 1.7, marginBottom: 40,
            opacity: 0, animation: 'hyp-fade-up 0.8s ease 0.3s forwards',
          }}>You can continue what you were doing — with more presence.</div>
          <button onClick={handleClose} style={{
            padding: '14px 40px', background: 'rgba(30,107,104,0.35)',
            border: '1px solid rgba(30,107,104,0.6)', borderRadius: 24,
            color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem', fontWeight: '600',
            cursor: 'pointer', letterSpacing: '0.04em',
            opacity: 0, animation: 'hyp-fade-up 0.8s ease 0.5s forwards',
          }}>Back</button>
        </div>
      )}

      <style>{`
        @keyframes hyp-fade-up {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
